import {
  OfferPublished,
  ReinsurersParticipation,
  UnreadMessages,
  UpdateOfferParticipationPercentage,
} from "graphql/__generated__/globalTypes";
import {
  acceptOffer,
  updateOfferParticipationPercentage,
  withdrawOfferParticipation,
} from "graphql/mutations/publish-offer";
import { FC, useMemo, useState } from "react";

import { useMutation } from "@apollo/client";
import { clsx } from "@mantine/core";
import { UpdateOfferParticipationPercentageMutationVariables } from "graphql/mutations/__generated__/publish-offer";
import ConfirmationModal from "./ConfirmationModal";
import DocumentViewerContainer from "./DocumentViewer";
import OfferThreadContainer from "./OfferThreadContainer";
import ParticipationHistoryContainer from "./ParticipationHistoryContainer";
import ParticipationModal from "./ParticipationModal";

interface Props {
  offer: OfferPublished;
  hasParticipated?: boolean;
  currentParticipation: ReinsurersParticipation | null;
  unreadMessages: (UnreadMessages | null)[] | null;
  onParticipate?: (offerId: string, share: number, conditions: string) => void;
  onRemoveParticipation?: (offerId: string) => void;
  refetchOffers?: () => void;
}

const ActionButtons: FC<Props> = ({
  offer,
  hasParticipated = false,
  currentParticipation,
  unreadMessages,
  onParticipate,
  onRemoveParticipation,
  refetchOffers,
}) => {
  // State management
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [showThreadContainer, setShowThreadContainer] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [showParticipationHistory, setShowParticipationHistory] =
    useState(false);

  // Derived state
  const isOfferClosed = offer.offer.offer_status === "CLOSED";
  const isEditAllowed =
    currentParticipation?.participation_status !== "ACCEPTED";
  const isExpired = offer.status.toUpperCase() === "EXPIRED";
  const isPlaceItOffer = offer.published_by?.toLowerCase() === "placeit";

  // Memoized values
  const unreadMessageCount = useMemo(
    () => unreadMessages?.filter((msg) => msg !== null).length || 0,
    [unreadMessages]
  );

  const unreadMessageIds = useMemo(
    () =>
      (unreadMessages
        ?.filter((msg) => msg !== null)
        .map((msg) => msg?.id)
        .filter(Boolean) as string[]) || [],
    [unreadMessages]
  );

  // GraphQL mutations
  const [acceptOfferMutation, { loading: acceptLoading }] = useMutation(
    acceptOffer,
    {
      onCompleted: () => {
        if (refetchOffers) refetchOffers();
        setIsModalOpen(false);
        setError(null);
      },
      onError: (error) => {
        setError(`Failed to participate in offer: ${error.message}`);
      },
      refetchQueries: ["GetPublishedOffers"],
    }
  );

  const [
    updateOfferParticipationPercentageMutation,
    { loading: editingLoading },
  ] = useMutation<
    UpdateOfferParticipationPercentage,
    UpdateOfferParticipationPercentageMutationVariables
  >(updateOfferParticipationPercentage, {
    onCompleted: () => {
      if (refetchOffers) refetchOffers();
      setIsModalOpen(false);
      setError(null);
    },
    onError: (error) => {
      setError(`Failed to update participation: ${error.message}`);
    },
    refetchQueries: ["GetPublishedOffers"],
  });

  const [removeParticipationMutation, { loading: removeLoading }] = useMutation(
    withdrawOfferParticipation,
    {
      onCompleted: () => {
        if (refetchOffers) refetchOffers();
        setShowRemoveConfirmation(false);
        setError(null);
      },
      onError: (error) => {
        setError(`Failed to withdraw participation: ${error.message}`);
        setShowRemoveConfirmation(false);
      },
      refetchQueries: ["GetPublishedOffers"],
    }
  );

  const openThreadContainer = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowThreadContainer(true);
  };

  const openParticipationHistory = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowParticipationHistory(true);
  };

  const handleParticipation = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOfferClosed && hasParticipated) {
      setError("Cannot edit participation for closed offers");
      return;
    }
    setIsEditing(hasParticipated);
    setIsModalOpen(true);
  };

  const viewDocuments = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDocumentViewer(true);
  };

  const handleRemoveParticipation = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOfferClosed) {
      setError("Cannot withdraw participation from closed offers");
      return;
    }
    setShowRemoveConfirmation(true);
  };

  const confirmRemoveParticipation = async () => {
    if (isOfferClosed) {
      setError("Cannot withdraw participation from closed offers");
      setShowRemoveConfirmation(false);
      return;
    }

    if (!currentParticipation?.offer_published_participant_id) {
      setError("Participation ID not found");
      setShowRemoveConfirmation(false);
      return;
    }

    setError(null);
    try {
      await removeParticipationMutation({
        variables: {
          input: {
            offer_published_id: offer.offer_published_id,
            policy_number: offer?.offer?.offer_detail?.policy_number,
            offer_published_participant:
              currentParticipation.offer_published_participant_id,
            published_by: offer?.published_by || "",
          },
        },
      });

      if (onRemoveParticipation) {
        onRemoveParticipation(
          isPlaceItOffer ? offer.offer.id! : offer.offer.offer_id!
        );
      }
    } catch (err) {
      console.error(
        "Error initiating withdrawing participation mutation:",
        err
      );
    }
  };

  const handleSubmitParticipation = async (
    share: number,
    conditions: string
  ) => {
    setError(null);
    try {
      await acceptOfferMutation({
        variables: {
          input: {
            offer_id: isPlaceItOffer ? offer.offer.id! : offer.offer.offer_id,
            offer_published_id: offer.offer_published_id,
            offer_participant_percentage: share,
            policy_number: offer?.offer?.offer_detail?.policy_number,
            message: conditions,
            loyalty_status: !isExpired,
            published_by: offer?.published_by || "",
          },
        },
      });

      if (onParticipate) {
        onParticipate(
          isPlaceItOffer ? offer.offer.id! : offer.offer.offer_id!,
          share,
          conditions
        );
      }
    } catch (err) {
      console.error("Error initiating accept offer mutation:", err);
    }
  };

  const handleEditParticipation = async (share: number) => {
    if (isOfferClosed) {
      setError("Cannot edit participation for closed offers");
      return;
    }

    setError(null);
    try {
      await updateOfferParticipationPercentageMutation({
        variables: {
          input: {
            offer_published_participant:
              currentParticipation?.offer_published_participant_id || "",
            offer_participation_percentage: share,
            policy_number: offer?.offer?.offer_detail?.policy_number || "",
            offer_published_id: offer.offer_published_id,
            published_by: offer?.published_by || "",
          },
        },
      });

      if (onParticipate) {
        onParticipate(
          isPlaceItOffer ? offer.offer.id! : offer.offer.offer_id!,
          share,
          ""
        );
      }
    } catch (err) {
      console.error("Error initiating accept offer mutation:", err);
    }
  };

  // Styling constants
  const baseButtonClass =
    "inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 ease-in-out transform hover:scale-105";

  const buttonClasses = {
    thread: clsx(
      baseButtonClass,
      "text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:ring-indigo-500"
    ),
    participate: clsx(
      baseButtonClass,
      "text-white bg-green-600 hover:bg-green-700 focus:ring-green-500"
    ),
    edit: clsx(
      baseButtonClass,
      "text-green-700 bg-green-100 hover:bg-green-200 focus:ring-green-500"
    ),
    docs: clsx(
      baseButtonClass,
      "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:ring-gray-400"
    ),
    history: clsx(
      baseButtonClass,
      "text-orange-700 bg-orange-100 hover:bg-orange-200 focus:ring-orange-500"
    ),
    remove: clsx(
      baseButtonClass,
      "text-white bg-red-600 hover:bg-red-700 focus:ring-red-500"
    ),
    disabled: clsx(
      baseButtonClass,
      "text-gray-400 bg-gray-100 cursor-not-allowed"
    ),
  };

  const iconClass = "w-5 h-5 mr-2";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-center items-center gap-3 p-4 bg-white rounded-lg shadow-sm">
        {!isOfferClosed && isEditAllowed && (
          <button
            onClick={handleParticipation}
            className={
              hasParticipated ? buttonClasses.edit : buttonClasses.participate
            }
            title={
              hasParticipated ? "Edit Participation" : "Participate in Offer"
            }
            disabled={acceptLoading}
          >
            <svg
              className={iconClass}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              {hasParticipated ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              )}
              {hasParticipated && (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              )}
            </svg>
            {hasParticipated ? "Edit" : "Participate"}
            {acceptLoading && (
              <span className="ml-1.5 animate-spin h-4 w-4 border-t-2 border-r-2 border-white rounded-full"></span>
            )}
          </button>
        )}

        <button
          onClick={openThreadContainer}
          className={`${buttonClasses.thread} relative`}
          title="Open Discussion Thread"
        >
          <svg
            className={iconClass}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          Thread
          {unreadMessageCount > 0 && (
            <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-orange-500 rounded-full">
              {unreadMessageCount}
            </span>
          )}
        </button>

        {isOfferClosed && hasParticipated && (
          <button
            className={buttonClasses.disabled}
            title="Cannot edit participation for closed offers"
            disabled={true}
          >
            <svg
              className={iconClass}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            Participation Locked
          </button>
        )}

        <button
          onClick={viewDocuments}
          className={buttonClasses.docs}
          title="View Associated Documents"
        >
          <svg
            className={iconClass}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          Docs
        </button>

        <button
          onClick={openParticipationHistory}
          className={buttonClasses.history}
          title="View Participation History & Audit Trail"
        >
          <svg
            className={iconClass}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          History
        </button>

        {!isOfferClosed && hasParticipated && isEditAllowed && (
          <button
            onClick={handleRemoveParticipation}
            className={buttonClasses.remove}
            title="Withdraw Participation"
            disabled={removeLoading}
          >
            <svg
              className={iconClass}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            Withdraw
            {removeLoading && (
              <span className="ml-1.5 animate-spin h-4 w-4 border-t-2 border-r-2 border-white rounded-full"></span>
            )}
          </button>
        )}
      </div>

      {error && (
        <div className="mt-2 text-sm text-red-600 text-center px-2">
          {error}
        </div>
      )}

      <ParticipationModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setError(null);
        }}
        offer={offer}
        isEditing={isEditing}
        currentParticipation={currentParticipation}
        onSubmit={handleSubmitParticipation}
        onEdit={handleEditParticipation}
        isOfferClosed={isOfferClosed}
        loading={acceptLoading || removeLoading || editingLoading}
      />

      <ConfirmationModal
        isOpen={showRemoveConfirmation}
        title="Confirm Withdrawal"
        message="Are you sure you want to withdraw your participation from this offer? This action cannot be undone."
        confirmButtonText={removeLoading ? "Withdrawing..." : "Withdraw"}
        cancelButtonText="Cancel"
        onConfirm={confirmRemoveParticipation}
        onCancel={() => setShowRemoveConfirmation(false)}
        isDestructive={true}
      />

      <OfferThreadContainer
        open={showThreadContainer}
        setOpen={setShowThreadContainer}
        offerPublished={offer}
        participationId={currentParticipation?.offer_published_participant_id!}
        unreadMessageIds={unreadMessageIds}
        refetch={refetchOffers}
      />

      <DocumentViewerContainer
        open={showDocumentViewer}
        setOpen={setShowDocumentViewer}
        offerId={isPlaceItOffer ? offer.offer.id! : offer.offer.offer_id!}
        offer={offer}
      />

      <ParticipationHistoryContainer
        open={showParticipationHistory}
        setOpen={setShowParticipationHistory}
        offerPublished={offer}
        offerId={isPlaceItOffer ? offer.offer.id! : offer.offer.offer_id!}
      />
    </div>
  );
};

export default ActionButtons;
